import React from 'react';
import { motion } from 'framer-motion';
import Icon from 'components/AppIcon';

const FloatingActionButton = ({ 
  onClick, 
  icon = 'Plus', 
  label = 'Add', 
  position = 'bottom-right',
  size = 'lg',
  className = '',
  showOnMobile = true,
  showOnDesktop = false
}) => {
  const positions = {
    'bottom-right': 'bottom-6 right-6',
    'bottom-left': 'bottom-6 left-6',
    'bottom-center': 'bottom-6 left-1/2 transform -translate-x-1/2',
    'top-right': 'top-6 right-6',
    'top-left': 'top-6 left-6'
  };

  const sizes = {
    sm: { container: 'w-10 h-10', icon: 14, text: 'text-xs' },
    md: { container: 'w-12 h-12', icon: 16, text: 'text-xs' },
    lg: { container: 'w-14 h-14', icon: 20, text: 'text-sm' }
  };

  const sizeConfig = sizes[size] || sizes.lg;
  
  const visibilityClasses = () => {
    if (showOnMobile && showOnDesktop) return '';
    if (showOnMobile && !showOnDesktop) return 'md:hidden';
    if (!showOnMobile && showOnDesktop) return 'hidden md:block';
    return 'hidden';
  };

  return (
    <motion.button
      onClick={onClick}
      className={`
        fixed ${positions[position]} ${visibilityClasses()}
        ${sizeConfig.container}
        bg-white/10 hover:bg-white/20
        text-white rounded-full
        shadow-lg hover:shadow-xl
        backdrop-filter backdrop-blur-lg
        border border-white/30
        transition-all duration-300
        flex items-center justify-center
        z-50
        group
        ${className}
      `}
      whileHover={{ scale: 1.1 }}
      whileTap={{ scale: 0.95 }}
      initial={{ opacity: 0, scale: 0.8, y: 20 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={{
        type: "spring",
        stiffness: 260,
        damping: 20,
        delay: 0.2
      }}
      aria-label={label}
    >
      <Icon 
        name={icon} 
        size={sizeConfig.icon} 
        className="text-white drop-shadow-sm" 
      />
      
      {/* Tooltip */}
      <motion.div
        className={`
          absolute right-full mr-4 top-1/2 transform -translate-y-1/2
          px-3 py-2 bg-black/80 text-white ${sizeConfig.text}
          rounded-lg border border-white/10
          backdrop-filter backdrop-blur-sm
          pointer-events-none
          whitespace-nowrap
          opacity-0 group-hover:opacity-100
          transition-opacity duration-200
        `}
      >
        {label}
        <div className="absolute left-full top-1/2 transform -translate-y-1/2 w-0 h-0 border-l-4 border-l-black/80 border-t-2 border-b-2 border-t-transparent border-b-transparent" />
      </motion.div>

      {/* Ripple effect */}
      <motion.div
        className="absolute inset-0 rounded-full bg-white/20"
        initial={{ scale: 0, opacity: 0 }}
        whileTap={{ scale: 1.2, opacity: 0.3 }}
        transition={{ duration: 0.2 }}
      />
    </motion.button>
  );
};

export default FloatingActionButton;